import React, { useEffect, useCallback } from 'react';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import FormLabel from '@material-ui/core/FormLabel';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import Typography from '@material-ui/core/Typography';
import { useDropzone } from 'react-dropzone';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ApiService from '../../../services/ApiService';
import {
  ticketNumberState,
  startLoading,
  stopLoading,
} from '../../HomePage/ContentTabs/ContentTabsSlice';
import helpers from './Helpers';
import TechnicalErrorAlert from '../../TechnicalErrorAlert/TechnicalErrorAlert';
import useStyles from './styles';
import DeleteFilePopUp from './DeleteFilePopUp';

const FileSaver = require('file-saver');

export default function FileUploader(props) {
  const classes = useStyles();
  const ticketNumber = useSelector(ticketNumberState);
  const {
    saveClicked,
    files,
    anomalyIndex,
    isEmailPopUp,
    isInvoicePopUp,
    isWagonSimulationPage,
  } = props;
  const [attachments, setAttachments] = React.useState([]);
  const [attachmentsWithfile, setAttachmentsWithFile] = React.useState([]);

  const [selectedAttachment, setSelectedAttachment] = React.useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    React.useState(false);
  const [deletedRegisteredFile, setDeletedRegisteredFile] =
    React.useState(null);
  const [openTechnicalErrorAlert, setOpenTechnicalErrorAlert] =
    React.useState(false);
  const [technicalErrorMessage, setTechnicalErrorMessage] =
    React.useState(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errors, setErros] = React.useState([]);
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    acceptedFiles.map((file) => {
      setSelectedAttachment(file);
      setErros(null);
      return false;
    });
    const errorList = [];
    rejectedFiles.map((rejectedFile) => {
      rejectedFile.errors.forEach((element) => {
        errorList.push(element.message);
      });
      setErros(errorList);
      return false;
    });
  }, []);

  const filenameLengthValidator = (file) => {
    console.log('file.name.length', file.name.length);
    if (file.name.length > 150) {
      return {
        code: 'name-too-large',
        message: t('filename_warning'),
      };
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    validator: filenameLengthValidator,
    onDrop,
    multiple: !isInvoicePopUp && !isWagonSimulationPage,
    maxSize: 20000000,
    accept:
      isInvoicePopUp || isWagonSimulationPage
        ? '.xls, .xlsx'
        : '.doc, .docx, .pdf, .xls, .xlsx, .csv, .ppt, .pptx, image/jpeg, image/png, .xml, .eml, .msg, .pst, .ost',
  });

  useEffect(() => {
    if (files != null) {
      files.map((file) => {
        if (!attachments.includes(file)) {
          attachments.push(file);
        }
        return false;
      });
    }
  }, []);

  useEffect(() => {
    props.sendAttachmentWithFiles(attachmentsWithfile);
  }, [attachmentsWithfile, saveClicked]);

  useEffect(() => {
    if (props.sendAttachments !== undefined) {
      props.sendAttachments(attachments);
    }
  }, [attachments, saveClicked]);

  useEffect(() => {
    if (selectedAttachment !== null) {
      if (
        attachments.filter((file) => file.filename === selectedAttachment.name)
          .length === 0
      ) {
        const selectedAttachmentInFileUploader = {
          filename: selectedAttachment.name,
          anomalyIndex: null,
        };
        if (isInvoicePopUp || isWagonSimulationPage) {
          const singleAttachment = [];
          const singleAttachmentWithFile = [];
          singleAttachment.push(selectedAttachmentInFileUploader);
          singleAttachmentWithFile.push(selectedAttachment);
          setAttachments(singleAttachment);
          setAttachmentsWithFile(singleAttachmentWithFile);
        } else {
          setAttachments(attachments.concat(selectedAttachmentInFileUploader));
          setAttachmentsWithFile(
            attachmentsWithfile.concat(selectedAttachment),
          );
        }
      }
    }
  }, [selectedAttachment]);

  const handleOpenDialog = (file) => {
    setOpenConfirmationDialog(true);
    setDeletedRegisteredFile(file);
  };

  const handleClose = () => {
    setOpenConfirmationDialog(false);
  };

  const handleCloseTechnicalErrorAlert = () => {
    setOpenTechnicalErrorAlert(false);
    setTechnicalErrorMessage(null);
  };

  function downloadFile(filename, indexAnomaly) {
    dispatch(startLoading());
    ApiService.getAttachment(
      ticketNumber,
      anomalyIndex || indexAnomaly,
      filename,
    )
      .then((response) => {
        const blob = new Blob([helpers.base64toBlob(response.data)], {});
        FileSaver.saveAs(blob, filename);
        dispatch(stopLoading());
      })
      .catch(() => {
        dispatch(stopLoading());
        setOpenTechnicalErrorAlert(true);
        setTechnicalErrorMessage(
          t('A technical error occured while downloading file') +
            filename +
            t('file could not be retrieved'),
        );
      });
  }
  function deleteRegisteredFile() {
    setOpenConfirmationDialog(false);
    dispatch(startLoading());
    ApiService.deleteAttachment(
      ticketNumber,
      anomalyIndex,
      deletedRegisteredFile,
    )
      .then(() => {
        setAttachments(
          attachments.filter((item) => item.filename !== deletedRegisteredFile),
        );
        dispatch(stopLoading());
      })
      .catch(() => {
        dispatch(stopLoading());
        setTechnicalErrorMessage(
          t('A technical error occured while deleting file') +
            deletedRegisteredFile +
            t('file could not be deleted'),
        );
        setOpenTechnicalErrorAlert(true);
      });
  }

  function deleteNewFile(filename) {
    setAttachments(attachments.filter((item) => item.filename !== filename));
    setAttachmentsWithFile(
      attachmentsWithfile.filter((item) => item.name !== filename),
    );
  }

  return (
    <Grid
      container
      spacing={1}
      className={clsx({
        [classes.gridMargin]:
          !isEmailPopUp && !isInvoicePopUp && !isWagonSimulationPage,
      })}
    >
      <Grid
        item
        xs={isEmailPopUp || isInvoicePopUp || isWagonSimulationPage ? 12 : 6}
        className={classes.titleMargin}
      >
        <FormLabel
          className={
            (classes.title,
            clsx({
              [classes.emailSubtitle]:
                isEmailPopUp || isInvoicePopUp || isWagonSimulationPage,
            }))
          }
        >
          {t('Upload your files')}
        </FormLabel>
      </Grid>
      {!isEmailPopUp && !isInvoicePopUp && !isWagonSimulationPage && (
        <Grid
          item
          xs={isEmailPopUp || isInvoicePopUp || isWagonSimulationPage ? 12 : 6}
          className={classes.titleMargin}
        >
          {attachments.length > 0 && (
            <FormLabel className={classes.formLabel}>
              {t('Uploaded files')}
            </FormLabel>
          )}
        </Grid>
      )}
      <Grid
        item
        xs={isEmailPopUp || isInvoicePopUp || isWagonSimulationPage ? 12 : 6}
      >
        <div
          {...getRootProps({
            className: `${classes.baseStyle} ${
              errors && errors.length > 0 ? classes.uploaderErrorBorder : ''
            }`,
          })}
        >
          <input {...getInputProps()} />
          <p className={classes.contentStyle}>
            {t('Drag and drop your files')}
          </p>
          <p className={classes.acceptedFileStyle}>
            {isInvoicePopUp || isWagonSimulationPage
              ? t('Handled formats for invoice')
              : t('Handled formats')}
            <br /> {t('Maximum file')}
          </p>

          {errors &&
            errors.length > 0 &&
            errors.map((error) => {
              return (
                <p key={error.message} className={classes.error}>
                  {t(error)}
                </p>
              );
            })}
        </div>
      </Grid>
      {(isEmailPopUp || isInvoicePopUp || isWagonSimulationPage) && (
        <Grid item xs={isEmailPopUp ? 12 : 6}>
          {attachments.length > 0 && (
            <FormLabel className={classes.emailFormLabel}>
              {t('Uploaded files')}
            </FormLabel>
          )}
        </Grid>
      )}
      <Grid
        item
        xs={isEmailPopUp || isInvoicePopUp || isWagonSimulationPage ? 12 : 6}
      >
        <ul className={classes.list}>
          {attachments.map((file, index) => {
            const fileId = `file-${index}`;
            return (
              attachments.length > 0 && (
                <li
                  key={fileId}
                  className={
                    files &&
                    files.filter(
                      (attachment) => attachment.filename === file.filename,
                    ).length > 0
                      ? classes.registeredFile
                      : classes.newFile
                  }
                >
                  <Grid container spacing={0}>
                    <Grid item xs={8} container alignItems="flex-end">
                      <Typography className={classes.uploadedFileName}>
                        {file.filename}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Grid container justify="flex-end" alignItems="flex-end">
                        {files &&
                          files.filter(
                            (attachment) =>
                              attachment.filename === file.filename,
                          ).length > 0 && (
                            <GetAppIcon
                              className={classes.downloadButton}
                              onClick={() =>
                                downloadFile(file.filename, file.anomalyIndex)
                              }
                            />
                          )}

                        <RemoveCircleOutlineIcon
                          className={classes.removeButton}
                          onClick={
                            files &&
                            files.filter(
                              (attachment) =>
                                attachment.filename === file.filename,
                            ).length > 0 &&
                            !isEmailPopUp &&
                            !isInvoicePopUp &&
                            !isWagonSimulationPage
                              ? () => handleOpenDialog(file.filename)
                              : () => deleteNewFile(file.filename)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </li>
              )
            );
          })}
        </ul>
      </Grid>
      <DeleteFilePopUp
        isEmailPopUp={isEmailPopUp}
        openConfirmationDialog={openConfirmationDialog}
        deletedRegisteredFile={deletedRegisteredFile}
        closePopUp={handleClose}
        confirmDelete={deleteRegisteredFile}
      />
      <TechnicalErrorAlert
        openTechnicalErrorAlert={openTechnicalErrorAlert}
        technicalErrorMessage={technicalErrorMessage}
        handleCloseTechnicalErrorAlert={handleCloseTechnicalErrorAlert}
      />
    </Grid>
  );
}
